import { Check, X } from "lucide-react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

export const BenefitsWhy = ({ showBenefits = true, showComparison = true, theme = "light", titleOverride }) => {
  const { benefits, why } = useProduct();
  const isBlue = theme === "blue";

  const sectionBg = isBlue ? "bg-riversol-blue" : "bg-white";
  const eyebrowColor = isBlue ? "text-white/70" : "text-riversol-blue";
  const titleColor = isBlue ? "text-white" : "text-neutral-900";
  const tableBorder = isBlue ? "border-white/20" : "border-neutral-200";
  const rowBorder = isBlue ? "border-white/15" : "border-neutral-200";
  const headerBg = isBlue ? "bg-white/[0.06]" : "bg-neutral-50";
  const headerRiversolCell = isBlue
    ? "bg-white text-riversol-blue"
    : "bg-riversol-blue text-white";
  const headerOtherCell = isBlue ? "text-white/50" : "text-neutral-400";
  const rowHover = isBlue ? "hover:bg-white/[0.04]" : "hover:bg-riversol-paper";
  const featureText = isBlue ? "text-white" : "text-neutral-900";
  const descText = isBlue ? "text-white/60" : "text-neutral-500";
  const checkCellBg = isBlue ? "bg-white/[0.08]" : "bg-riversol-blue/[0.04]";
  const checkCircle = isBlue
    ? "bg-white text-riversol-blue"
    : "bg-riversol-blue text-white";
  const xCircle = isBlue
    ? "border border-white/25 text-white/40"
    : "border border-neutral-200 text-neutral-300";

  return (
    <section
      className={`${sectionBg} py-20 lg:py-32`}
      data-testid={isBlue ? "benefits-why-blue" : "benefits-why"}
      data-inverted-bg={isBlue ? "blue" : undefined}
    >
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        {showBenefits && (
          <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${showComparison ? "mb-20 lg:mb-40" : ""}`}>
            {/* Image now sits at the RIGHT on desktop (mobile keeps image
                first for visual anchor). Reversed via lg:order-2. */}
            <div className="lg:col-span-5 relative order-1 lg:order-2">
              <div className="editorial-frame aspect-[4/5] bg-riversol-sand group" style={{ clipPath: "inset(0 round 15px)" }}>
                <img src={benefits.image} alt={benefits.imageAlt} className="w-full h-full object-cover img-zoom" />
              </div>
            </div>
            <div className="lg:col-span-7 lg:pr-6 order-2 lg:order-1">
              <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
                {benefits.eyebrow}
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900 mb-8 lg:mb-12">
                {benefits.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
              </h2>
              <div className="grid grid-cols-1 gap-px">
                {benefits.items.map((b, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <div className="flex items-start gap-5 py-5 border-t border-neutral-200">
                      <Check size={18} className="text-riversol-blue mt-1 shrink-0" strokeWidth={2} />
                      <span className="text-[16px] leading-relaxed text-neutral-700">{b}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Why Riversol Is Different */}
        {showComparison && (
        <div>
          <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-16">
            <div className={`pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase ${eyebrowColor} mb-6`}>
              {why.eyebrow}
            </div>
            <h2 className={`text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold ${titleColor}`}>
              {titleOverride || why.title}
            </h2>
          </div>
          {why.comparison ? (
            <Reveal>
              <div className={`max-w-4xl mx-auto border ${tableBorder} overflow-hidden`}>
                <div className={`grid grid-cols-[1fr_60px_60px] lg:grid-cols-[1fr_150px_150px] ${headerBg} border-b ${rowBorder}`}>
                  <div className="px-4 lg:px-8 py-5" />
                  <div className={`px-2 lg:px-4 py-5 text-center ${headerRiversolCell} font-mono text-[10px] lg:text-[12px] tracking-[0.14em] uppercase`}>Riversol</div>
                  <div className={`px-2 lg:px-4 py-5 text-center font-mono text-[10px] lg:text-[12px] tracking-[0.14em] uppercase ${headerOtherCell}`}>Others</div>
                </div>
                {why.comparison.map((row, i) => (
                  <div key={i} className={`grid grid-cols-[1fr_60px_60px] lg:grid-cols-[1fr_150px_150px] border-b ${rowBorder} last:border-b-0 items-center ${rowHover} transition-colors`}>
                    <div className="px-4 lg:px-8 py-5 lg:py-6">
                      <div className={`text-base lg:text-lg ${featureText} leading-snug`}>{row.feature}</div>
                      {row.desc && <div className={`text-[12px] lg:text-[13px] ${descText} mt-1.5 leading-relaxed max-w-md`}>{row.desc}</div>}
                    </div>
                    <div className={`px-2 lg:px-4 py-5 lg:py-6 flex justify-center ${checkCellBg} h-full items-center`}>
                      <span className={`flex items-center justify-center w-7 h-7 lg:w-8 lg:h-8 rounded-full ${checkCircle}`}>
                        <Check size={14} strokeWidth={2.5} />
                      </span>
                    </div>
                    <div className="px-2 lg:px-4 py-5 lg:py-6 flex justify-center items-center">
                      <span className={`flex items-center justify-center w-7 h-7 lg:w-8 lg:h-8 rounded-full ${xCircle}`}>
                        <X size={14} strokeWidth={2} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
              {why.items.map((d, i) => (
                <Reveal key={i} delay={(i % 3) * 0.08} className="bg-white">
                  <div className="px-8 py-12 h-full flex flex-col gap-4">
                    <span className="font-mono text-sm text-riversol-blue">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-lg leading-snug text-neutral-800">{d}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </section>
  );
};

export default BenefitsWhy;
